import { ArrowLeft } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import type { ReactNode } from "react";
import { useBackNav, type BackTo } from "./useBackNav";

/**
 * 页头左侧的「← 返回」。单独导出给没走 PageHeader 的页头(视频资产库 / BGM 管理 /
 * 海报模板管理 / 分身创建页状态条)—— 视觉与 PageHeader 内嵌的一模一样,别再各画各的。
 */
export type { BackTo } from "./useBackNav";

export function PageBackButton({
  backTo,
  beforeBack,
  className,
}: {
  backTo: BackTo;
  /** 离开前的闸(如「有未保存的修改」确认):回 false 就留在本页。 */
  beforeBack?: () => boolean | Promise<boolean>;
  className?: string;
}) {
  const goBack = useBackNav(backTo);
  const onClick = async () => {
    if (beforeBack && !(await beforeBack())) return;
    goBack();
  };
  return (
    <button
      type="button"
      onClick={() => void onClick()}
      aria-label="返回"
      data-testid="page-back"
      className={`flex shrink-0 items-center gap-1 rounded-md px-2 py-1.5 text-xs text-muted-foreground transition-colors hover:bg-primary/10 hover:text-primary ${className ?? ""}`}
    >
      <ArrowLeft className="h-4 w-4" />
      返回
    </button>
  );
}

export interface PageHeaderProps {
  /** 页头图标(lucide),统一渲染在 bg-primary/10 圆角盒内。 */
  icon: LucideIcon;
  title: string;
  /** 标题下方一行说明;省略则不渲染。 */
  subtitle?: ReactNode;
  /** 右侧操作区(按钮组等)。 */
  actions?: ReactNode;
  /**
   * 给了就在 icon 盒左侧渲染「← 返回」(三层兜底见 useBackNav)。
   * 一级页别给 —— 一级页没有上级,返回按钮只会把人送去一个莫名其妙的地方。
   */
  backTo?: BackTo;
  /** 点「← 返回」时先问一句(未保存改动等);回 false 就不走。只在给了 `backTo` 时有意义。 */
  beforeBack?: () => boolean | Promise<boolean>;
}

/**
 * 统一页头(房子标准,对齐 PosterDashboard / KnowledgeBase):
 * icon 盒 + text-xl 粗标题 + 副标题 + 右侧 actions 槽。
 * 收敛此前三种各行其是的页头风(军师 text-2xl / 数字人 text-lg / 裸 icon 无盒)。
 */
export function PageHeader({
  icon: Icon,
  title,
  subtitle,
  actions,
  backTo,
  beforeBack,
}: PageHeaderProps) {
  return (
    // ★ flex-wrap + actions 可换行(2026-07-24 H-A 门实测):原 `shrink-0` 让操作区**永不换行**,
    // 三个按钮的海报页头在 <400px 视口横向溢出 81px(实测 320px 溢出 81 / 390px 溢出 11)——
    // 整页跟着能左右拖。actions 改为允许换行并右对齐,窄屏自然掉到第二行。
    <div className="flex flex-wrap items-center justify-between gap-3 fx-enter">
      <div className="flex min-w-0 flex-1 basis-64 items-center gap-3">
        {backTo !== undefined && <PageBackButton backTo={backTo} beforeBack={beforeBack} />}
        <div className="shrink-0 rounded-lg bg-primary/10 p-2.5">
          <Icon className="h-5 w-5 text-primary" />
        </div>
        <div className="min-w-0">
          <h1 className="text-xl font-bold text-foreground">{title}</h1>
          {/* ★subtitle 用 div 不用 p(0915):副标常塞 Badge(渲染为 div),div 嵌进 p 是无效
              HTML —— 浏览器会就地把 p 截断,React 也在开发态报 validateDOMNesting。
              Tailwind preflight 已把 p 的默认边距清零,换成 div 视觉零差异。 */}
          {subtitle && <div className="mt-0.5 text-sm text-muted-foreground">{subtitle}</div>}
        </div>
      </div>
      {actions && <div className="flex flex-wrap items-center justify-end gap-2">{actions}</div>}
    </div>
  );
}
